import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { CheckCircle, FolderOpen, HelpCircle, Key, Rocket, Webhook } from 'lucide-react';

const FAQ_STEP_INDEX = 4;

const SetupSidebar = ({ selectedStep, onStepChange }) => {
  const steps = [
    {
      label: 'Login',
      icon: <Key size={18} />,
    },
    {
      label: 'Project Creation',
      icon: <FolderOpen size={18} />,
    },
    {
      label: 'Webhook Integration',
      icon: <Webhook size={18} />,
    },
    {
      label: 'Getting Started',
      icon: <Rocket size={18} />,
    },
    {
      label: 'FAQs',
      icon: <HelpCircle size={18} />,
      isFaq: true,
    },
  ];

  const getStepStatus = (stepIndex) => {
    if (stepIndex === FAQ_STEP_INDEX) return stepIndex === selectedStep ? 'active' : 'pending';
    if (stepIndex < selectedStep) return 'completed';
    if (stepIndex === selectedStep) return 'active';
    return 'pending';
  };

  const getStepIcon = (stepIndex, status) => {
    if (status === 'completed') return <CheckCircle size={18} color="#21c45d" />;
    if (status === 'active') return steps[stepIndex].icon;
    return steps[stepIndex].icon;
  };

  const getStepBackground = (status) => {
    if (status === 'completed') return '#21c45d1a'; // Light green
    if (status === 'active') return '#3c83f6'; // Blue
    return 'transparent';
  };

  const getStepTextColor = (status) => {
    if (status === 'completed') return '#21c45d'; // Green
    if (status === 'active') return '#ffffff'; // White
    return '#6b7280'; // Gray
  };

  const getStepBorder = (status) => {
    if (status === 'active') return '0 solid #1d4ed8';
    return 'none';
  };

  return (
    <Box sx={{ 
      background: '#fff', 
      color: '#1e293b', 
      p: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }, 
      minHeight: { xs: 'auto', md: 'calc(100vh - 56px)' }, 
      height: { md: 'calc(100vh - 72px)' },
      width: '100%',
      borderBottom: { xs: '1px solid #e2e8f0', md: 'none' },
      position: { md: 'sticky' },
      top: { md: '56px' },
      overflowY: { md: 'auto' },
      overflowX: 'hidden'
    }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1, letterSpacing: 1, color: '#000', fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
        Setup Guide
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 2, md: 4 }, fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.775rem' } }}>
        Get your AI code review assistant up and running.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'row', md: 'column' }, gap: { xs: 1, md: 2 }, overflowX: { xs: 'auto', md: 'visible' }, pb: { xs: 1, md: 0 } }}>
        {steps.map((step, idx) => {
          const status = getStepStatus(idx);
          return (
            <React.Fragment key={step.label}>
              {step.isFaq && (
                <Divider sx={{ display: { xs: 'none', md: 'block' }, my: 1, borderColor: '#e2e8f0' }} />
              )}
            <Box
              onClick={() => onStepChange(idx)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: '0.5rem', md: '1rem' },
                pt: { xs: '0.5rem', md: '0.75rem' },
                pb: { xs: '0.5rem', md: '0.75rem' },
                pl: { xs: '0.75rem', md: '1rem' },
                pr: { xs: '0.75rem', md: '1rem' },
                borderRadius: 2,
                cursor: 'pointer',
                background: getStepBackground(status),
                color: getStepTextColor(status),
                border: getStepBorder(status),
                transition: 'all 0.2s ease',
                minWidth: { xs: 'fit-content', md: 'auto' },
                flexShrink: { xs: 0, md: 1 },
                '&:hover': {
                  background: status === 'active' ? '#3c83f6' : status === 'completed' ? '#bbf7d0' : '#f1f5f9',
                }
              }}
            >
              <Box sx={{ minWidth: { xs: 20, md: 30 }, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundClip: '#f3f4f6' }}>
                <Box sx={{
                  width: { xs: 20, sm: 22, md: 30 },
                  height: { xs: 20, sm: 22, md: 30 },
                  borderRadius: '50%',
                  bgcolor: status === 'active' ? '#fff3' : status === 'completed' ? '#bbf7d0' : '#f1f5f9',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    '& svg': {
                      width: { xs: 16, sm: 18 },
                      height: { xs: 16, sm: 18 }
                    }
                  }}>
                    {getStepIcon(idx, status)}
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Typography variant="body1" sx={{
                  fontWeight: 500,
                  fontSize: { xs: '0.875rem', md: '1rem' }
                }}>
                  {step.label}
                </Typography>
                <Typography variant="caption" sx={{ 
                  color: status === 'active' ? '#e0e7ff' : status === 'completed' ? '#21c45d' : '#9ca3af',
                  fontSize: { xs: '0.7rem', md: '0.75rem' }
                }}>
                  {step.isFaq ? 'Help' : `Step ${idx + 1}`}
                </Typography>
              </Box>
            </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

export default SetupSidebar;
